import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';

import { map } from 'rxjs/operators';

import { Observable } from 'rxjs';

import { CamRoutes } from '@camelot/menu';
import { Logger } from '@camelot/server';

import { CamPermissionsService, Domain, Level, Permission, PermissionFeature } from '../services/permissions.service';

export interface FeatureRouteData {
  feature?: PermissionFeature | Domain | string;
  level?: Level;
  permissions?: (Permission | string)[];
}
@Injectable({
  providedIn: 'root',
})
export class FeatureGuard {
  public readonly _permissionsService = inject(CamPermissionsService);
  private router = inject(Router);

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean> | boolean {
    return this._check(route);
  }

  canActivateChild(route: ActivatedRouteSnapshot): Observable<boolean> | boolean {
    return this._check(route);
  }

  public setRedirect(): void {
    this.router.navigateByUrl(CamRoutes.getHome());
  }

  private _check(route: ActivatedRouteSnapshot): Observable<boolean> | boolean {
    const data = this._routeData(route);
    if (!data) {
      Logger.LogError('[FeatureGuard] Route guarded without feature/level or permissions data', route.url.join('/'));
      this.setRedirect();
      return false;
    }

    if (this._permissionsService.received === true) {
      return this._isValidPermission(data);
    }
    return this._permissionsService.updated$.pipe(map(() => this._isValidPermission(data)));
  }

  // Child routes do not inherit data, so walk up to the route that declared the guard.
  private _routeData(route: ActivatedRouteSnapshot | null): FeatureRouteData | null {
    for (let current = route; current; current = current.parent) {
      const { feature, level, permissions } = current.data as FeatureRouteData;
      if ((feature !== undefined && level !== undefined) || permissions?.length) {
        return { feature, level, permissions };
      }
    }
    return null;
  }

  private _isValidPermission(data: FeatureRouteData) {
    const featureGranted =
      data.feature === undefined || data.level === undefined
        ? true
        : this._permissionsService.canDirectAccess(data.feature, data.level);
    const permissionsGranted = (data.permissions ?? []).every(permission => {
      const [verb, resource] = permission.split(':');
      return this._permissionsService.canDirectAccess(resource, verb);
    });
    if (featureGranted && permissionsGranted) {
      return true;
    }
    this.setRedirect();
    return false;
  }
}
